import { uiPaper } from "shared/ui/uiSurface"
import { ContentCopy } from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useStart2faEnrollMutation,
  useVerify2faMutation,
} from "src/features/profile/profileApi"

interface TwoFactorSetupDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export const TwoFactorSetupDialog: FC<TwoFactorSetupDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation()
  const [startEnroll, { isLoading: enrollLoading }] =
    useStart2faEnrollMutation()
  const [verify2fa, { isLoading: verifyLoading }] = useVerify2faMutation()

  const [ticketUrl, setTicketUrl] = useState<string | null>(null)
  const [code, setCode] = useState("")
  const [error, setError] = useState("")

  const handleStartEnroll = async () => {
    try {
      const result = await startEnroll().unwrap()
      setTicketUrl(result.data.ticketUrl)
      setError("")
    } catch {
      setError("Failed to start enrollment")
    }
  }

  const handleVerify = async () => {
    if (code.length !== 6 || !/^\d+$/.test(code)) {
      setError(t("profile.2fa.invalidCode"))
      return
    }
    try {
      const result = await verify2fa({ code }).unwrap()
      if (result.data.enrolled) {
        onSuccess()
        handleClose()
      } else {
        setError(t("profile.2fa.invalidCode"))
      }
    } catch {
      setError(t("profile.2fa.invalidCode"))
    }
  }

  const handleClose = () => {
    setTicketUrl(null)
    setCode("")
    setError("")
    onClose()
  }

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DLV2-167") }}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t("profile.2fa.setup.title")}</DialogTitle>
      <DialogContent>
        {!ticketUrl ? (
          // Step 1: Start enrollment
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t("profile.2fa.description")}
            </Typography>
            <Button
              variant="contained"
              onClick={handleStartEnroll}
              disabled={enrollLoading}
              sx={{ textTransform: "none" }}
            >
              {enrollLoading ? (
                <CircularProgress size={20} />
              ) : (
                t("profile.2fa.setup.startSetup")
              )}
            </Button>
          </Stack>
        ) : (
          // Step 2: Complete enrollment at Auth0 + verify
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {t("profile.2fa.setup.openLink")}
            </Typography>
            <Box
              sx={{
                p: 2,
                bgcolor: "grey.50",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="body2"
                  sx={{
                    wordBreak: "break-all",
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    flex: 1,
                  }}
                >
                  {ticketUrl}
                </Typography>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => navigator.clipboard.writeText(ticketUrl)}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.open(ticketUrl, "_blank")}
              sx={{ textTransform: "none", alignSelf: "flex-start" }}
            >
              {t("profile.2fa.setup.openEnrollment")}
            </Button>

            <Typography variant="body2" sx={{ mt: 2 }}>
              {t("profile.2fa.setup.enterCode")}
            </Typography>
            <TextField
              label={t("profile.2fa.setup.verificationCode")}
              value={code}
              onChange={(e) => {
                setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                setError("")
              }}
              size="small"
              inputProps={{ maxLength: 6, inputMode: "numeric" }}
              error={!!error}
              helperText={error}
              sx={{ maxWidth: 200 }}
              autoFocus
            />
          </Stack>
        )}

        {error && !ticketUrl && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          {t("common.cancel")}
        </Button>
        {ticketUrl && (
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={verifyLoading || code.length !== 6}
            sx={{ textTransform: "none" }}
          >
            {verifyLoading ? (
              <CircularProgress size={20} />
            ) : (
              t("profile.2fa.setup.verify")
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
